import { cva, type VariantProps } from "class-variance-authority";
import {
  Button as AriaButton,
  composeRenderProps,
  type ButtonProps as AriaButtonProps,
} from "react-aria-components";
import { forwardRef, type ForwardedRef } from "react";
import { tw } from "src/design-system/tw";

export const buttonVariants = cva(
  [
    "inline-flex cursor-pointer items-center justify-center gap-2 rounded-md",
    "data-disabled:cursor-default data-disabled:opacity-50",
    "data-focus-visible:z-10 data-focus-visible:outline-2 data-focus-visible:outline-offset-2",
  ],
  {
    variants: {
      variant: {
        primary: [
          "bg-fill-brand font-medium text-text-on-bg-fill",
          "data-hovered:bg-fill-brand-hover",
          "data-pressed:bg-fill-brand-active",
        ],
        secondary: [
          "bg-fill-primary font-medium text-text-primary",
          "data-hovered:bg-fill-primary-hover",
          "data-pressed:bg-fill-primary-active",
        ],
        subtle: [
          "bg-fill-solid font-medium text-text-primary underline",
          "data-hovered:bg-fill-secondary-hover",
          "data-pressed:bg-fill-secondary-active",
        ],
        elevated: [
          "bg-fill-solid font-medium text-text-primary shadow-sm",
          "data-hovered:bg-fill-solid-hover",
          "data-pressed:bg-fill-solid-active",
        ],
      },
      size: {
        small: "p-2 text-label-md",
        medium: "px-3 py-2.5 text-label-md",
        large: "px-4 py-3 text-label-lg",
      },
    },
    defaultVariants: {
      variant: "primary",
      size: "medium",
    },
  },
);

export type ButtonProps = AriaButtonProps & VariantProps<typeof buttonVariants>;

function ButtonInner(
  { className, variant, size, ...props }: ButtonProps,
  ref: ForwardedRef<HTMLButtonElement>,
) {
  return (
    <AriaButton
      className={composeRenderProps(className, (className) =>
        tw(
          buttonVariants({
            variant,
            size,
            className,
          }),
        ),
      )}
      {...props}
      ref={ref}
    />
  );
}

export const Button = forwardRef(ButtonInner);
Button.displayName = "Button";
